/**
 * Preferences store for app visibility, pinning, and ordering.
 * Pseudocode:
 * - Load/save from localStorage under a namespaced key
 * - Expose a writable store with { order: string[], pinned: string[], hidden: string[] }
 * - Helpers to pin/unpin, hide/unhide, reorder
 */
import { writable } from 'svelte/store';

export type Prefs = {
  order: string[]; // explicit order of app ids
  pinned: string[]; // pinned app ids
  hidden: string[]; // hidden app ids
  categories: Record<string, string>; // appId -> category name
  categoryOrder: string[]; // ordering of category sections
  collapsed: string[]; // collapsed category names
};

const KEY = 'spa-studio.prefs.v1';
const DEFAULT_PINNED = ['pdf-merge', 'redact-pdf', 'pdf-protect', 'catan'];

function createDefaultPrefs(): Prefs {
  return {
    order: [],
    pinned: [...DEFAULT_PINNED],
    hidden: [],
    categories: {},
    categoryOrder: [],
    collapsed: []
  };
}

function load(): Prefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return createDefaultPrefs();
    const parsed = JSON.parse(raw);
    return {
      order: Array.isArray(parsed.order) ? parsed.order : [],
      pinned: Array.isArray(parsed.pinned) ? parsed.pinned : [...DEFAULT_PINNED],
      hidden: Array.isArray(parsed.hidden) ? parsed.hidden : [],
      categories: parsed.categories && typeof parsed.categories === 'object' ? parsed.categories : {},
      categoryOrder: Array.isArray(parsed.categoryOrder) ? parsed.categoryOrder : [],
      collapsed: Array.isArray(parsed.collapsed) ? parsed.collapsed : []
    } as Prefs;
  } catch {
    return createDefaultPrefs();
  }
}

function save(p: Prefs) {
  try { localStorage.setItem(KEY, JSON.stringify(p)); } catch {}
}

export const prefs = writable<Prefs>(load());
prefs.subscribe(save);

export function resetPrefs() {
  const p = createDefaultPrefs();
  save(p);
  prefs.set(p);
}

export function isPinned(p: Prefs, id: string) { return p.pinned.includes(id); }
export function isHidden(p: Prefs, id: string) { return p.hidden.includes(id); }

export function togglePin(id: string) {
  prefs.update((p) => {
    const i = p.pinned.indexOf(id);
    if (i === -1) p.pinned = [...p.pinned, id];
    else p.pinned = p.pinned.filter((x) => x !== id);
    return p;
  });
}

export function toggleHidden(id: string) {
  prefs.update((p) => {
    const i = p.hidden.indexOf(id);
    if (i === -1) p.hidden = [...p.hidden, id];
    else p.hidden = p.hidden.filter((x) => x !== id);
    return p;
  });
}

export function setOrder(order: string[]) {
  prefs.update((p) => ({ ...p, order: [...order] }));
}

export function setCategory(appId: string, category: string) {
  prefs.update((p) => ({ ...p, categories: { ...p.categories, [appId]: category } }));
}

export function addCategory(name: string) {
  prefs.update((p) => ({ ...p, categoryOrder: p.categoryOrder.includes(name) ? p.categoryOrder : [...p.categoryOrder, name] }));
}

export function moveCategory(name: string, dir: -1 | 1) {
  prefs.update((p) => {
    const idx = p.categoryOrder.indexOf(name);
    if (idx === -1) return p;
    const j = idx + dir; if (j < 0 || j >= p.categoryOrder.length) return p;
    const arr = p.categoryOrder.slice();
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return { ...p, categoryOrder: arr } as Prefs;
  });
}

export function toggleCategoryCollapsed(name: string) {
  prefs.update((p) => {
    const has = p.collapsed.includes(name);
    return { ...p, collapsed: has ? p.collapsed.filter((c) => c !== name) : [...p.collapsed, name] } as Prefs;
  });
}

export function renameCategory(oldName: string, newName: string) {
  if (!newName || oldName === newName) return;
  prefs.update((p) => {
    const categories = { ...p.categories };
    for (const [appId, cat] of Object.entries(categories)) {
      if (cat === oldName) categories[appId] = newName;
    }
    let order = p.categoryOrder.slice();
    const idx = order.indexOf(oldName);
    if (idx !== -1) {
      order.splice(idx, 1, newName);
    } else if (!order.includes(newName)) {
      order.push(newName);
    }
    const collapsed = p.collapsed.includes(oldName)
      ? [...p.collapsed.filter((c) => c !== oldName), newName]
      : p.collapsed;
    return { ...p, categories, categoryOrder: order, collapsed } as Prefs;
  });
}

export function deleteCategory(name: string) {
  prefs.update((p) => {
    const categories = { ...p.categories };
    for (const [appId, cat] of Object.entries(categories)) {
      if (cat === name) delete categories[appId]; // revert to default
    }
    const order = p.categoryOrder.filter((c) => c !== name);
    const collapsed = p.collapsed.filter((c) => c !== name);
    return { ...p, categories, categoryOrder: order, collapsed } as Prefs;
  });
}
